import axiosInstance from '../config';

/**
 * Requests a launch URL from the backend for SABA platform.
 * @param {string} [gameId] - Optional sub-game ID
 * @returns {Promise<string|null>} - The launch URL, or null if session expired / invalid
 */
export const launchSaba = async (gameId) => {
  const params = gameId ? { gameId } : {};
  const res = await axiosInstance.get('restricted/saba/launch', { params });
  let data = res && res.data;

  // If response body is a JSON string, parse it
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === 'object') {
        data = parsed;
      }
    } catch (e) {
      // Not JSON, continue treating as plain text
    }
  }

  // If response is an object with { status: true, url: '...' }
  if (data && typeof data === 'object') {
    if (data.url) return data.url;
    if (data.data && data.data.url) return data.data.url;
  }

  // If response is a direct URL string
  if (typeof data === 'string' && data !== 'null' && data.startsWith('http')) {
    return data;
  }

  return null;
};

/**
 * Requests the SABA game catalogue, so the player picks a game here instead of landing on SABA's homepage.
 * The vendor call is signed on the backend - the partner secret never reaches the browser.
 * @param {string} [category] - Optional vendor category ("Slot", "Lottory", ...)
 * @returns {Promise<Array>} - The games SABA will launch, in the vendor's own order
 */
export const fetchSabaGames = async (category) => {
  const params = category ? { category } : {};
  const res = await axiosInstance.get('restricted/saba/games', { params });
  let data = res && res.data;

  // If response body is a JSON string, parse it
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      throw new Error('Game list unavailable');
    }
  }

  // status:false is the backend saying it could not read the catalogue - not an empty catalogue
  if (!data || typeof data !== 'object' || data.status !== true || !Array.isArray(data.list)) {
    throw new Error('Game list unavailable');
  }

  // A game the vendor marks inactive will not launch, so it is not offered
  return data.list.filter((game) => game && game.active !== false);
};

/**
 * Whether this player may use WeGa at all.
 *
 * The backend's own gates are the real ones - this only keeps the player's screens from offering what the
 * platform would refuse anyway. Answered true unless the backend says otherwise, so a failed read hides
 * nothing: a menu item that is shown and then refused is a smaller lie than a game that is hidden from a
 * player who is allowed to play it.
 *
 * @returns {Promise<boolean>} - false only when the backend says the line is switched off
 */
export const fetchSabaAccess = async () => {
  try {
    const res = await axiosInstance.get('restricted/saba/access');
    let data = res && res.data;

    // If response body is a JSON string, parse it
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return true;
      }
    }

    return !(data && typeof data === 'object' && data.allowed === false);
  } catch (e) {
    return true;
  }
};

/**
 * Requests the vendor's trade-detail page for one of the player's own game bets.
 *
 * The vendor's answer is a one-shot ticket token behind the URL, so it is asked for when the player opens the
 * detail and never stored: no cache, no retry, nothing kept between two opens. An open bet has a page too - the
 * vendor shows it as unsettled rather than refusing - so this is offered on every row of the games half.
 *
 * @param {string} pubId - the public id of the history row the player is looking at
 * @returns {Promise<string|null>} - The vendor's page, or null when there is no detail to show
 */
export const fetchSabaTradeDetail = async (pubId) => {
  const res = await axiosInstance.get('restricted/saba/tradeDetail', {
    params: { pubId },
  });
  let data = res && res.data;

  // If response body is a JSON string, parse it
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return null;
    }
  }

  // status:false is the backend saying it could not produce a page - the same answer for a bet that is not
  // this player's and for a vendor that cannot be reached, so there is nothing more to tell the screen
  if (
    data &&
    typeof data === 'object' &&
    data.status === true &&
    typeof data.url === 'string' &&
    data.url.startsWith('http')
  ) {
    return data.url;
  }

  return null;
};
